import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PopupWheelContent, PopupWheelSegment } from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import type { WheelStatus } from "./WheelStage";

interface CardsStageProps {
  wheel: PopupWheelContent;
  segments: PopupWheelSegment[];
  step: ResolvedStepDesign;
  panelIsLight: boolean;
  status: WheelStatus;
  /** Segment the server picked; read while status === "result". */
  resultIndex: number | null;
  /** The visitor tapped a card — the widget treats it exactly like "spin". */
  onPick?: (cardIndex: number) => void;
  onLanded?: (index: number) => void;
  onSettled?: () => void;
  inert?: boolean;
}

const FLIP_MS = 600;
const FOLLOW_MS = 420;

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Pick-a-card — the wheel's third game (the Alia "card game"). Face-down
 * cards; the visitor taps one and it flips to the prize the SERVER chose
 * (same segments, same odds, same status lifecycle as the wheel — nothing
 * here decides anything). "match" then flips the remaining cards so three
 * of them show the prize and the rest show the other wedges; "pick" opens
 * only the tapped card (a mystery box) unless cards_reveal_others is set.
 */
export const CardsStage: React.FC<CardsStageProps> = ({
  wheel,
  segments,
  step,
  panelIsLight,
  status,
  resultIndex,
  onPick,
  onLanded,
  onSettled,
  inert = false,
}) => {
  const count = Math.min(6, Math.max(2, Math.round(wheel.cards_count ?? 4)));
  const mode = wheel.cards_mode === "pick" ? "pick" : "match";
  // Match mode: does the visitor flip each card themselves (one per tap,
  // until three match) or does the deck finish itself after the first tap?
  const manual = mode === "match" && wheel.cards_flip === "tap";
  const w = Math.min(200, Math.max(48, wheel.card_width_px ?? 72));
  const h = Math.min(300, Math.max(48, wheel.card_height_px ?? 108));
  const radius = Math.max(0, wheel.card_radius_px ?? 12);
  const backText = (wheel.card_back_text ?? "?").trim() || "?";
  const button = step.elements.button;
  const backBg = wheel.card_back_color || (panelIsLight ? "#ffffff" : "rgba(255,255,255,0.96)");
  const backFg = wheel.card_back_text_color || button.background;
  const faceBg = wheel.card_face_color || button.background;
  const faceFg = wheel.card_face_text_color || button.textColor;
  const hint = (wheel.cards_hint_text ?? "Tap a card").trim();

  const [picked, setPicked] = useState<number | null>(null);
  // Which segment each card shows once flipped (null = still face down).
  const [faces, setFaces] = useState<(number | null)[]>(() => Array(count).fill(null));
  // Manual play: what every card WILL show when tapped (decided at result time).
  const planRef = useRef<number[] | null>(null);
  const timersRef = useRef<number[]>([]);
  const landedRef = useRef(false);
  const clearTimers = () => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  // Fresh round (also after an error: the tapped card settles back).
  useEffect(() => {
    if (status === "idle" || status === "error") {
      clearTimers();
      planRef.current = null;
      setFaces(Array(count).fill(null));
      if (status === "idle") setPicked(null);
      landedRef.current = false;
      if (status === "idle") onSettled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, count]);

  // The reveal: the tapped card first, then (mode-dependent) the rest.
  useEffect(() => {
    if (status !== "result" || resultIndex === null || landedRef.current) return;
    const first = picked ?? 0;
    const others = Array.from({ length: count }, (_, i) => i).filter((i) => i !== first);
    // Decoys cycle through the OTHER wedges so every face reads as a real prize.
    const decoys = segments.map((_, i) => i).filter((i) => i !== resultIndex);
    const winners = new Set<number>();
    if (mode === "match") {
      // Three matching cards in total (fewer when the deck is smaller), at
      // random positions so a manual player cannot learn where they sit.
      const shuffled = others.slice().sort(() => Math.random() - 0.5);
      shuffled.slice(0, Math.max(0, Math.min(2, count - 1))).forEach((i) => winners.add(i));
    }
    const plan: { card: number; seg: number }[] = [{ card: first, seg: resultIndex }];
    const reveal = mode === "match" || wheel.cards_reveal_others;
    if (reveal) {
      let d = 0;
      others.forEach((card) => {
        const seg = winners.has(card)
          ? resultIndex
          : decoys.length
            ? decoys[d++ % decoys.length]
            : resultIndex;
        plan.push({ card, seg });
      });
    }
    if (manual) {
      // Only the tapped card flips now; the rest wait for the visitor's taps.
      const byCard = Array<number>(count).fill(resultIndex);
      plan.forEach((p) => { byCard[p.card] = p.seg; });
      planRef.current = byCard;
      setFaces((prev) => { const next = prev.slice(); next[first] = resultIndex; return next; });
      return;
    }
    const instant = reducedMotion();
    const stepMs = instant ? 0 : FOLLOW_MS;
    plan.forEach((p, n) => {
      const t = window.setTimeout(() => {
        setFaces((prev) => {
          const next = prev.slice();
          next[p.card] = p.seg;
          return next;
        });
      }, n === 0 ? 0 : (instant ? 0 : FLIP_MS) + (n - 1) * stepMs);
      timersRef.current.push(t);
    });
    const total = instant ? 0 : FLIP_MS + Math.max(0, plan.length - 1) * stepMs + 350;
    const done = window.setTimeout(() => {
      if (landedRef.current) return;
      landedRef.current = true;
      onLanded?.(resultIndex);
    }, total);
    timersRef.current.push(done);
    return () => {
      /* timers are cleared on the next idle/error, not on re-render */
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, resultIndex]);

  // Manual play after the reveal: any face-down card can be tapped until
  // three cards match (or the deck runs out), then the round lands.
  const winnersShown = resultIndex === null ? 0 : faces.filter((f) => f === resultIndex).length;
  const manualLive = manual && status === "result" && !!planRef.current && !landedRef.current;
  const canTap = (status === "idle" && !inert && !!onPick) || (manualLive && !inert);
  const revealManual = (i: number) => {
    const plan = planRef.current;
    if (!plan || faces[i] !== null || resultIndex === null) return;
    const next = faces.slice();
    next[i] = plan[i];
    setFaces(next);
    const shown = next.filter((f) => f === resultIndex).length;
    const flippedAll = next.every((f) => f !== null);
    if (shown >= 3 || flippedAll) {
      const t = window.setTimeout(() => {
        if (landedRef.current) return;
        landedRef.current = true;
        onLanded?.(resultIndex);
      }, reducedMotion() ? 0 : FLIP_MS + 350);
      timersRef.current.push(t);
    }
  };
  const cards = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);
  const cols = count <= 3 ? count : count === 4 ? 4 : 3;

  return (
    <div className="flex w-full flex-col items-center" data-cards-stage>
      <div
        className="grid justify-center"
        style={{ gridTemplateColumns: `repeat(${cols}, ${w}px)`, gap: Math.max(8, Math.round(w * 0.14)) }}
        role="group"
        aria-label="Pick a card"
      >
        {cards.map((i) => {
          const face = faces[i];
          const flipped = face !== null;
          const isPicked = picked === i;
          const lifting = isPicked && status === "spinning";
          return (
            <button
              key={i}
              type="button"
              disabled={!canTap || (manualLive && flipped)}
              aria-label={flipped && face !== null ? segments[face]?.label : `Card ${i + 1}`}
              onClick={() => {
                if (!canTap) return;
                if (manualLive) {
                  revealManual(i);
                  return;
                }
                setPicked(i);
                onPick?.(i);
              }}
              style={{
                width: w,
                height: h,
                perspective: 900,
                background: "transparent",
                border: 0,
                padding: 0,
                cursor: canTap ? "pointer" : "default",
                transform: lifting ? "translateY(-8px) scale(1.04)" : "none",
                transition: "transform 250ms ease",
                outline: "none",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  transformStyle: "preserve-3d",
                  transition: reducedMotion() ? "none" : `transform ${FLIP_MS}ms cubic-bezier(.2,.7,.2,1)`,
                  transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: radius,
                    background: wheel.card_back_image_url
                      ? `url("${wheel.card_back_image_url}") center/cover no-repeat, ${backBg}`
                      : backBg,
                    color: backFg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: Math.round(Math.min(w, h) * 0.42),
                    fontWeight: 700,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  {wheel.card_back_image_url ? null : backText}
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: radius,
                    background: faceBg,
                    color: faceFg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: Math.round(w * 0.1),
                    fontSize: Math.max(11, Math.round(Math.min(w, h) * 0.2)),
                    fontWeight: 700,
                    lineHeight: 1.1,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                    transform: "rotateY(180deg)",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  {face !== null ? segments[face]?.label : ""}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {hint && (status === "idle" || manualLive) && (
        <p
          className="mt-3 text-sm"
          style={{ color: step.elements.subheading.color, opacity: 0.8 }}
        >
          {manualLive && winnersShown > 0 ? (wheel.cards_keep_tapping_text ?? "Tap another card") : hint}
        </p>
      )}
    </div>
  );
};

export default CardsStage;
